import {TokenStorage} from '../../../Services/security/TokenStorage';
import {TokenStore} from '../../../Services/security/TokenStore';
import {AuthAPIDataSource} from '../api/AuthAPIDataSource';
import {LoginCredentials} from './AuthRepository';
import {mapUserDTOToModel} from '../mappers/auth.mapper';
import {AuthRepository} from './AuthRepository';
import {User, ProviderData} from '../models/User';
import {AppError, ErrorType} from '../../../Data/config/AppError';
import {UserRole} from '../models/User';
import {ServiceProviderAPIDataSource} from '../../serviceProviders/api/ServiceProviderAPIDataSource';
import {mapMyBrandsResponseToProviderData} from '../../serviceProviders/utils/providerDataMapper';
import type {GetCurrentUserResponseDTO} from '../models/auth.dto';
import type {UserDTO} from '../models/auth.dto';

export interface LoginResult {
  user: User;
  message: string;
}

export class AuthRepositoryImpl implements AuthRepository {
  private readonly authDataSource: AuthAPIDataSource;
  private readonly tokenStorage: TokenStorage;

  constructor () {
    this.authDataSource = new AuthAPIDataSource();
    this.tokenStorage = new TokenStorage();
  }

  async login (credentials: LoginCredentials): Promise<LoginResult> {
    try {
      const result = await this.authDataSource.login(credentials);

      // result now contains {data: LoginData, stat_message: string}
      if (!result.data?.accessToken) {
        throw new AppError('لم يتم استلام رمز الوصول', ErrorType.AUTHENTICATION);
      }

      await this.tokenStorage.setToken(result.data.accessToken);

      if (result.data.refreshToken) {
        TokenStore.setRefreshToken(result.data.refreshToken);
      }

      // After successful login, fetch complete user data with permissions
      const user = await this.getCurrentUser();

      const finalResult = {
        user,
        message: result.stat_message
      };

      return finalResult;
    } catch (error) {
      // Don't handle error here - just throw it so it can be handled at the component level
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async logout (): Promise<void> {
    try {
      const result = await this.authDataSource.logout();

      if (result.stat_code !== 200) {
        throw new AppError(result.stat_message || 'فشل تسجيل الخروج', ErrorType.AUTHENTICATION);
      }
    } catch (error) {
      // Don't handle error here - just throw it so it can be handled at the component level
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    } finally {
      await this.tokenStorage.removeToken();
    }
  }

  /**
   * Fetch provider data using the new /provider/my-brands endpoint
   * This endpoint returns the current logged-in provider's brands
   * The endpoint may return either:
   * 1. A MyBrands object with structure: { providerId, providerName, brands: [...] }
   * 2. An array of brands directly
   */
  private async fetchProviderDataFromMyBrands (userId: number): Promise<ProviderData | undefined> {
    try {
      const myBrandsResponse = await ServiceProviderAPIDataSource.getMyBrands();

      return mapMyBrandsResponseToProviderData(myBrandsResponse, userId);
    } catch (error) {
      // Silently fail - provider data is optional
      // Don't throw to prevent login failure
      return undefined;
    }
  }

  private async fetchUserPermissions (userId: number): Promise<string[]> {
    try {
      const effectivePermissionsResponse = await this.authDataSource.getUserEffectivePermissions(userId);
      if (Array.isArray(effectivePermissionsResponse) && effectivePermissionsResponse.length > 0) {
        type EffectivePermissionDTO = {
          name?: string;
          permission_name?: string;
        };

        return (effectivePermissionsResponse as EffectivePermissionDTO[])
          .map((permission) => permission?.name ?? permission?.permission_name ?? '')
          .filter((permissionName): permissionName is string => typeof permissionName === 'string' && permissionName.length > 0);
      }
    } catch (permError) {
      // Failed to load effective permissions - user will have limited access
    }
    return [];
  }

  private async buildUserDTO (result: GetCurrentUserResponseDTO): Promise<UserDTO> {
    if (!result.data) {
      throw new AppError('لم يتم العثور على بيانات المستخدم', ErrorType.NOT_FOUND);
    }

    let permissions: string[] = [];

    // First, check if permissions are already in the /users/me response
    if (result.data.permissions && Array.isArray(result.data.permissions)) {
      permissions = result.data.permissions;
    } else {
      // Fallback: Try to fetch permissions from effective permissions endpoint
      if (result.data.id) {
        permissions = await this.fetchUserPermissions(result.data.id);
      }
    }

    const userDTO = {
      id: result.data.id || 1,
      username: result.data.username || '',
      full_name: result.data.full_name || result.data.username || 'User',
      email: result.data.email || '',
      role: result.data.role?.name || 'admin',
      role_id: result.data.role?.id || 1,
      permissions,
      is_active: result.data.status === 'active',
      created_at: result.data.created_at || new Date().toISOString(),
      updated_at: result.data.updated_at || new Date().toISOString()
    };

    return userDTO;
  }

  async getCurrentUser (): Promise<User> {
    try {
      const result = await this.authDataSource.getCurrentUser();

      if (result.stat_code !== 200) {
        throw new AppError(result.stat_message || 'فشل جلب بيانات المستخدم', ErrorType.API_ERROR);
      }

      if (!result.data) {
        throw new AppError('لم يتم العثور على بيانات المستخدم', ErrorType.NOT_FOUND);
      }

      const userDTO = await this.buildUserDTO(result);

      // Check if user is a provider and fetch provider data (assigned brands)
      let providerData: ProviderData | undefined = undefined;
      const roleName = result.data.role?.name ? String(result.data.role.name).toLowerCase() : '';
      const username = result.data.username ? String(result.data.username).toLowerCase() : '';
      const providerRoleNames = new Set<string>([
        UserRole.PROVIDER,
        'service_provider',
        'service-provider',
        'serviceprovider'
      ]);
      const isProviderByRole = roleName ? providerRoleNames.has(roleName) : false;
      const isProviderByUsername = username.includes('_provider') || username.includes('provider_');
      const isProvider = isProviderByRole || isProviderByUsername;

      if (isProvider && result.data.id) {
        providerData = await this.fetchProviderDataFromMyBrands(result.data.id);
      }

      return mapUserDTOToModel(userDTO, providerData);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }
}
